import React, { createContext, useContext, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Snackbar, Alert, Box, Slide } from '@mui/material';

interface Toast {
    id: string;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
    duration?: number;
}

interface ToastContextProps {
    showToast: (message: string, severity?: 'success' | 'error' | 'info' | 'warning', duration?: number) => void;
    dismissToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextProps | undefined>(undefined);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [toasts, setToasts] = useState<Toast[]>([]);

    const showToast = (message: string, severity: 'success' | 'error' | 'info' | 'warning' = 'info', duration = 3000) => {
        const id = uuidv4();
        setToasts(prev => [...prev, { id, message, severity, duration }]);
    };

    const dismissToast = (id: string) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    };

    return (
        <ToastContext.Provider value={{ showToast, dismissToast }}>
            {children}
            <Box
                sx={{
                    position: 'fixed',
                    top: 16,
                    right: 16,
                    zIndex: 1400,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                }}
            >
                {toasts.map((toast) => (
                    <Slide
                        key={toast.id}
                        direction="left"
                        in
                        mountOnEnter
                        unmountOnExit
                        timeout={{ enter: 400, exit: 300 }}
                    >
                        <Snackbar
                            open
                            autoHideDuration={toast.duration}
                            onClose={() => dismissToast(toast.id)}
                            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                        >
                            <Alert
                                onClose={() => dismissToast(toast.id)}
                                severity={toast.severity}
                                variant="filled"
                                sx={{
                                    width: '100%',
                                    boxShadow: 3,
                                    transform: 'scale(1)',
                                    transition: 'transform 0.2s ease',
                                    '&:hover': {
                                        transform: 'scale(1.03)',
                                    },
                                }}
                            >
                                {toast.message}
                            </Alert>
                        </Snackbar>
                    </Slide>
                ))}
            </Box>
        </ToastContext.Provider>
    );
};

export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};
